import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory, useRouteMatch } from 'react-router-dom';
import { ArrowLeft, Briefcase, Loader2, CalendarDays, FileText, MapPin, Clock, ChevronRight, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';

import CustomerLayout   from '../../CustomerLayout';
import ShiftDetailPanel from './ShiftDetailPanel';
import NewWorkOrderSlideOver from '../NewWorkOrderSlideOver';

import {
  getCustomerCompanyJobById,
  getCustomerShiftDetail,
} from '../../../../../services/staffingService';

const JOB_STATUS = {
  open:      { bg: 'bg-sky-100',     text: 'text-sky-700',     label: 'Open'      },
  completed: { bg: 'bg-gray-100',    text: 'text-gray-600',    label: 'Completed' },
  paid:      { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid'      },
  disputed:  { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Disputed'  },
  cancelled: { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Cancelled' },
};

const SHIFT_STATUS = {
  open:    { bg: 'bg-red-100',     text: 'text-red-700',     label: 'Open'    },
  filled:  { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Filled'  },
  partial: { bg: 'bg-amber-100',   text: 'text-amber-700',   label: 'Partial' },
};

// Top-level tabs — Shifts and Invoice only.
// Live Patrol and Activity Logs live inside ShiftDetailPanel (shift-scoped).
const TABS = [
  { key: 'shifts',  label: 'Shifts',  Icon: CalendarDays },
  { key: 'invoice', label: 'Invoice', Icon: FileText      },
];

function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

// Extract HH:MM in the job's stored timezone (IANA) for shift template pre-fill
function extractTimeHHMM(isoString, timezone) {
  if (!isoString) return '08:00';
  const tz = timezone || 'America/New_York';
  const result = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  }).format(new Date(isoString));
  // Handle midnight edge case where Intl returns '24:00'
  return result.replace(/^24:/, '00:');
}

// Deduplicate shift templates by (startTime, endTime, staffsRequired) — avoids
// 90 identical rows for a recurring schedule. Capped at 5 unique patterns.
function buildPrefillData(job) {
  if (!job) return null;
  const seen = new Set();
  const shiftTemplates = (job.shifts || [])
    .map(s => ({
      startTime:      extractTimeHHMM(s.startTime, job.timezone),
      endTime:        extractTimeHHMM(s.endTime,   job.timezone),
      staffsRequired: s.staffsRequired || 1,
    }))
    .filter(t => {
      const key = `${t.startTime}|${t.endTime}|${t.staffsRequired}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5);

  return {
    serviceType:    job.serviceType    || '',
    description:    job.memo           || '',
    hourlyPayRate:  job.hourlyPayRate  || '',
    facilityId:     job.facilityId     || '',
    shiftTemplates,
  };
}

/**
 * CustomerJobDetail — MASTER ORCHESTRATOR for the work order detail page.
 *
 * All service calls live here. Children receive data via props and fire
 * callbacks (onRefresh, onClose) to trigger re-fetches.
 *
 * URL shapes handled without remounting:
 *   /customer-company/:id/jobs/:jobId
 *   /customer-company/:id/jobs/:jobId/shift/:shiftId
 *
 * Layout:
 *   Left panel  — shift list (narrows when a shift is selected)
 *   Right panel — ShiftDetailPanel (Officers / Live Patrol / Activity Logs sub-tabs)
 */
const CustomerJobDetail = () => {
  const { customerCompanyId, jobId } = useParams();
  const history = useHistory();

  const shiftRouteMatch = useRouteMatch(
    '/customer-company/:customerCompanyId/jobs/:jobId/shift/:shiftId'
  );
  const selectedShiftId = shiftRouteMatch?.params?.shiftId || null;

  const [job,          setJob]          = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('shifts');
  const [shiftDetail,  setShiftDetail]  = useState(null);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [repeatOpen,   setRepeatOpen]   = useState(false);

  // ── Fetch job (all fetches owned here) ──────────────────────────────────
  const fetchJob = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getCustomerCompanyJobById(customerCompanyId, jobId);
      setJob(resp.data);
    } catch (err) {
      console.error('[CustomerJobDetail] fetchJob error:', err);
      toast.error('Failed to load work order');
    } finally {
      setLoading(false);
    }
  }, [customerCompanyId, jobId]);

  useEffect(() => { fetchJob(); }, [fetchJob]);

  // ── Fetch shift detail when URL has /shift/:shiftId ─────────────────────
  const fetchShiftDetail = useCallback(async () => {
    if (!selectedShiftId) { setShiftDetail(null); return; }
    setShiftLoading(true);
    try {
      const resp = await getCustomerShiftDetail(customerCompanyId, jobId, selectedShiftId);
      setShiftDetail(resp.data);
    } catch (err) {
      console.error('[CustomerJobDetail] fetchShiftDetail error:', err);
    } finally {
      setShiftLoading(false);
    }
  }, [customerCompanyId, jobId, selectedShiftId]);

  useEffect(() => { fetchShiftDetail(); }, [fetchShiftDetail]);

  // Auto-select first shift when page loads with no shift in URL
  useEffect(() => {
    if (!loading && !selectedShiftId && job?.shifts?.length > 0 && activeTab === 'shifts') {
      history.replace(`/customer-company/${customerCompanyId}/jobs/${jobId}/shift/${job.shifts[0]._id}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, job, selectedShiftId, activeTab]);

  const handleShiftClick  = (shift) => history.push(`/customer-company/${customerCompanyId}/jobs/${jobId}/shift/${shift._id}`);
  const handleClosePanel  = ()      => history.push(`/customer-company/${customerCompanyId}/jobs/${jobId}`);

  const panelOpen = !!selectedShiftId;

  // ── Invoice tab (job-scoped, inline) ────────────────────────────────────
  const InvoiceTab = () => {
    const inv = job?.invoice;
    if (!inv?.id) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <FileText size={32} className="mb-3 opacity-30" />
          <p className="text-sm">No invoice generated yet</p>
          <p className="text-xs opacity-60 mt-1">Invoice will appear here once the job is completed</p>
        </div>
      );
    }
    return (
      <div className="space-y-4 max-w-lg">
        <div className="rounded-xl border border-border bg-card p-5 space-y-3">
          {[
            ['Invoice ID', inv.id],
            ['Amount', inv.amountTotal ? `$${(inv.amountTotal / 100).toFixed(2)} ${(inv.currency || 'usd').toUpperCase()}` : '—'],
            ['Due Date', inv.dueDate ? fmtDate(inv.dueDate) : '—'],
            ['Status', inv.status || '—'],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-medium text-foreground capitalize">{value}</span>
            </div>
          ))}
        </div>
        {inv.url && (
          <a
            href={inv.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-lg bg-[#CC457D] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#CC457D]/90 transition-colors w-fit"
          >
            <FileText size={14} /> View Invoice
          </a>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <CustomerLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      </CustomerLayout>
    );
  }

  const jobStatus = JOB_STATUS[job?.status] || JOB_STATUS.open;
  const shifts    = job?.shifts || [];

  return (
    <CustomerLayout>
      <div className="flex h-full flex-col overflow-hidden">

        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div className="border-b border-border bg-card px-6 py-4 flex-shrink-0">
          <button
            onClick={() => history.push(`/customer-company/${customerCompanyId}/jobs`)}
            className="mb-3 flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={14} /> Back to Work Orders
          </button>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#404880]/10 flex-shrink-0">
                <Briefcase size={18} className="text-[#404880]" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">{job?.location || 'Work Order'}</h1>
                <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                  <MapPin size={10} /> {job?.formattedAddress || job?.city}
                  {job?.serviceType  && <> &middot; {job.serviceType}</>}
                  {job?.hourlyPayRate && <> &middot; ${job.hourlyPayRate}/hr</>}
                </p>
              </div>
            </div>
            <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${jobStatus.bg} ${jobStatus.text}`}>
              {jobStatus.label}
            </span>
            <button
              onClick={() => setRepeatOpen(true)}
              title="Repeat this work order with the same details"
              className="flex-shrink-0 flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-[#404880] hover:text-[#404880] transition-colors"
            >
              <RotateCcw size={13} /> Repeat Order
            </button>
          </div>

          {/* Job-level tabs: Shifts · Invoice */}
          <div className="mt-4 flex gap-1">
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); if (key !== 'shifts') handleClosePanel(); }}
                className={`relative flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors rounded-t-lg ${
                  activeTab === key ? 'text-[#CC457D]' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={13} /> {label}
                {activeTab === key && (
                  <motion.div
                    layoutId="customer-job-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t bg-[#CC457D]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content area ────────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden">

          {activeTab === 'shifts' && (
            <>
              {/* Left: shift list — narrows when panel is open */}
              <div className={`flex flex-col overflow-hidden border-r border-border bg-card transition-all duration-300 ${
                panelOpen ? 'w-80 flex-shrink-0' : 'flex-1'
              }`}>
                <div className="flex-1 overflow-y-auto">
                  {shifts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                      <CalendarDays size={28} className="mb-2 opacity-30" />
                      <p className="text-sm">No shifts scheduled</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {shifts.map((shift) => {
                        const isSelected = shift._id?.toString() === selectedShiftId;
                        const cfg        = SHIFT_STATUS[shift.status] || SHIFT_STATUS.open;
                        return (
                          <button
                            key={shift._id}
                            onClick={() => handleShiftClick(shift)}
                            className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/50 ${
                              isSelected ? 'border-l-2 border-[#CC457D] bg-[#CC457D]/5' : 'border-l-2 border-transparent'
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-foreground">{fmtDate(shift.date)}</p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock size={10} /> {fmtTime(shift.startTime)} – {fmtTime(shift.endTime)}
                              </p>
                              <p className="mt-1 text-[10px] text-muted-foreground">
                                {shift.activeOfferCount ?? 0}/{shift.staffsRequired} officers
                              </p>
                            </div>
                            <div className="flex-shrink-0 flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${cfg.bg} ${cfg.text}`}>
                                {cfg.label}
                              </span>
                              <ChevronRight size={14} className="text-muted-foreground" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: ShiftDetailPanel with Officers / Live Patrol / Activity sub-tabs */}
              <AnimatePresence>
                {panelOpen && (
                  <motion.div
                    key="shift-panel"
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 24 }}
                    transition={{ duration: 0.2 }}
                    className="flex-1 overflow-hidden bg-card"
                  >
                    {shiftLoading ? (
                      <div className="flex h-full items-center justify-center">
                        <Loader2 size={24} className="animate-spin text-muted-foreground" />
                      </div>
                    ) : shiftDetail ? (
                      <ShiftDetailPanel
                        shift={shiftDetail.shift}
                        job={shiftDetail.job}
                        offers={shiftDetail.offers || []}
                        activityLogs={shiftDetail.activityLogs || []}
                        filledCount={shiftDetail.filledCount || 0}
                        onClose={handleClosePanel}
                        onRefresh={fetchShiftDetail}
                        customerCompanyId={customerCompanyId}
                      />
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}

          {activeTab === 'invoice' && (
            <div className="flex-1 overflow-y-auto p-6">
              <InvoiceTab />
            </div>
          )}

        </div>
      </div>

      {/* Repeat Order slide-over — pre-fills all fields from this job */}
      <NewWorkOrderSlideOver
        open={repeatOpen}
        onClose={() => setRepeatOpen(false)}
        onCreated={() => {
          setRepeatOpen(false);
          toast.success('Repeat work order created!');
        }}
        prefillData={buildPrefillData(job)}
      />
    </CustomerLayout>
  );
};

export default CustomerJobDetail;
